import { getCompatibleCodesForSystem } from 'elements/hardwareSelector/SearchToolbar'
import { useMemo } from 'react'
import type { ComponentTypesV2 } from 'types/selectComponent'
import type {
  ConfigurableFilterOptionsType,
  FilterComponentNodeV2,
  FilterGenericPropsType,
  FilterValuesType,
  OptionGenericType,
} from '../typeV2'
import { parseStringifiedListFilterValue } from '../utils/utils'

export const INVERTER_COMPATIBILITY_FILTER_NODE_KEY_V2 = 'inverter_compatibility_v2'
export const CODES_FILTER_KEY = 'codes'

const INVERTER_COMPATIBILITY_OPTIONS: OptionGenericType<string>[] = [
  { id: 'default', title: 'All', value: '' },
  { id: 'yes', title: 'Yes', value: 'true' },
]

export const createInverterCompatibilityFilterNode = <T,>({
  label = 'Compatible with system',
  rendererComponent: RendererComponent,
  compatibleCodes = [],
  ...restConfigs
}: ConfigurableFilterOptionsType<string, string> & {
  compatibleCodes?: string[]
}): FilterComponentNodeV2 => {
  const FilterComponent = ({
    allFilterValues,
    modifiableFilterValues,
    setModifiableFilters,
  }: FilterGenericPropsType) => {
    // Use provided compatibleCodes or fall back to system-based lookup
    const codes = useMemo(() => {
      if (compatibleCodes && compatibleCodes.length > 0) {
        return compatibleCodes
      }
      return getCompatibleCodesForSystem('charger')
    }, [compatibleCodes])

    const types = parseStringifiedListFilterValue<ComponentTypesV2>({
      filterValues: allFilterValues,
      key: 'types',
    })
    const displayFilter = types.has('inverter') && codes.length !== 0

    if (!displayFilter) {
      return null
    }

    const onSelect = (value) => {
      if (value === 'true') {
        setModifiableFilters({
          ...modifiableFilterValues,
          [CODES_FILTER_KEY]: codes.join(','),
        })
      } else {
        if (!!modifiableFilterValues[CODES_FILTER_KEY]) {
          const newModifiableFilterValues = { ...modifiableFilterValues }
          delete newModifiableFilterValues[CODES_FILTER_KEY]
          setModifiableFilters(newModifiableFilterValues)
        }
      }
    }

    const getInverterCompatibilityValue = ({ filterValues }: { filterValues: FilterValuesType }) => {
      const filterCodes = filterValues[CODES_FILTER_KEY]
      const compatible = !!filterCodes && codes.join(',') === filterCodes
      return compatible ? 'true' : ''
    }

    return (
      <RendererComponent
        id={INVERTER_COMPATIBILITY_FILTER_NODE_KEY_V2}
        defaultValue={''}
        label={label}
        options={INVERTER_COMPATIBILITY_OPTIONS}
        disabled={false}
        onSelect={onSelect}
        value={getInverterCompatibilityValue({ filterValues: allFilterValues })}
      />
    )
  }

  return {
    key: INVERTER_COMPATIBILITY_FILTER_NODE_KEY_V2,
    component: FilterComponent,
  }
}

export default createInverterCompatibilityFilterNode
